import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  bxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

export const getBillCycleConsumerDashboard = async (
  params: Record<string, any>
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "bill-cycle/consumer-dashboard",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toString());
      qs.push("is_category_count", 1);

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            qs.push("premise", item);
          });
        } else if (value !== undefined && value !== null) {
          qs.push(key, String(value));
        }
      });
    }
  );

  const response = await bxApiClient.get(url);
  return response?.data?.result;
};

export const getBillCycleList = async (
  queryParams: Record<string, any>
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "billschedule",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("is_bill_cycle", 1);
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          qs.push(key, String(value));
        }
      });
    }
  );

  const response = await bxApiClient.get(url);
  return response.data;
};

export const exportBillCycleList = async (
  queryParams: Record<string, any>
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "billschedule",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("is_bill_cycle", 1);

      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          qs.push(key, String(value));
        }
      });
      
      qs.push("export_format", "csv");
    }
  );

  const response = await bxApiClient.get(url);
  return response.data;
};

export const getBillCycleDetails = async (
  billCycleId: string | number
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill-cycle/details`,
    (qs: QueryStringParameters) => {
      qs.push("bill_cycle_id", billCycleId);
    }
  );

  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getBillCycleDetailsForEdit = async (
  billCycleId: string | number
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billcycle/details/${billCycleId}`
  );

  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getCycleWiseRun = async (
  billCycleId: string | number,
  page: number,
  search: string
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill-run`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId);
      qs.push("page", page);
      qs.push("extra_detail", 1);
      if (search) {
        qs.push("search_data", search);
      }
    }
  );

  const response = await bxApiClient.get(url);
  return response.data;
};

export const getMonthlyStats = async (
  billCycleId: string | number
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `monthly-stats`,
    (qs: QueryStringParameters) => {
      qs.push("bill_cycle_id", billCycleId);
    }
  );

  const response = await bxApiClient.get(url);
  return response.data.result;
};


export const getPremiseList = async (
  queryParams: Record<string, any> = {}
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const isSearch = !!queryParams.search_data;

  // Helper to push common pagination params
  const addPaginationParams = (qs: QueryStringParameters) => {
    qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    qs.push("limit", PAGE_SIZE);
    qs.push("is_pagination_required", true);
    qs.push("page", queryParams.page || 1);
  };

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    isSearch ? "search/onboarding_config" : "config-map/territory_json",
    (qs: QueryStringParameters) => {
      addPaginationParams(qs);

      if (isSearch) {
        qs.push("search_field", "territory_json-premise");
        qs.push("search_data", queryParams.search_data);
      } else {
        qs.push("config_level", "premise");

        Object.entries(queryParams).forEach(([key, value]) => {
          if (!value) return; // skip falsy values

          if (key === "sub_area" && Array.isArray(value)) {
            qs.push("code_list", value.join(","));
          } else {
            qs.push(key, String(value));
          }
        });
      }
    }
  );

  const response = await bxApiClient.get(url);
  return response.data;
};


export const getCategoryConfigMap = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `config-map/category_json`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("config_level", "category");
    }
  );
  const response = await onboardingApiClient.get(url);

  const data = response.data?.result;
  return data.map((item: any) => ({
    value: item.code,
    label: item.name,
  }));
};

export const getSubCategoryConfigMap = async (
  codes: string[]
): Promise<any> => {
  console.log("subCategory received", codes);

  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/category_json",
    (qs) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("config_level", "sub_category");
      if (codes) {
        // If filter is an array, push each element
        if (Array.isArray(codes)) {
          if (Array.isArray(codes)) {
            codes.forEach((code) => {
              qs.push("code_list", code);
            });
          }
        }
      }
    }
  );
  const response = await onboardingApiClient.get(url);
  const data = response.data?.result;

  return data.map((item: any) => ({
    id: item.code,
    name: item.name,
  }));
};

export const getConsumerList = async (
  queryParams: Record<string, any>,
  billCycleId?: string | number
): Promise<any> => {
  console.log("premise selected:", queryParams);
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "billcycle/get-consumer-list-view-data",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);

      if (billCycleId) {
        qs.push("bill_cycle_id", billCycleId);
      }

      qs.push("is_pagination", 1);
      qs.push("is_extra_details", "1");
      Object.entries(queryParams).forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          value !== "" &&
          key !== "export" &&
          (!Array.isArray(value) || value.length > 0)
        ) {
          if (Array.isArray(value)) {
            value.forEach((v) => {
              qs.push(key, v);
            });
          } else {
            qs.push(key, value);
          }
        }
      });
      // ✅ Add export_format if export === true
      if (queryParams.export === true) {
        qs.push("export_format", "csv");
      }
    }
  );

  const response = await bxApiClient.get(url);

  const consumers = response.data.result.map((item: any) => ({
    ...item,
    consumerInfo: {
      consumerName: `${item.firstName} ${item.lastName}`,
      accountNo: item.remoteConsumerNumber,
    },
    address: { area: item.area, subArea: item.subArea },
  }));

  return {
    ...response.data,
    result: consumers,
  };
};

export const getBillSummary = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill-cycle/summary`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    }
  );

  const response = await bxApiClient.get(url);
  return response.data.result;
};

export const submitBillCycle = async (payload: any) => {
  const url = ApiEndpoints.createUrlWithQueryParameters("bx", `billcycle`);
  const response = await bxApiClient.post(url, payload);
  return response.data;
};

export const updateBillCycle = async (payload: any, billCycleId) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billcycle/${billCycleId}`
  );
  const response = await bxApiClient.put(url, payload);
  return response.data;
};

export const getTerritoryConfigMap = async (
  configLevel: "zone"|"division"|"area" | "sub_area" | "premise" = "zone",
  codeList?: string[]
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `config-map/territory_json`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("config_level", configLevel);
      if (codeList && codeList.length > 0) {
        codeList.forEach((code) => {
          qs.push("code_list", code);
        });
      }
    }
  );
  const response = await onboardingApiClient.get(url);

  const data = response.data?.result;

  return data.map((item: any) => ({
    id: item.code,
    name: item.name,
  }));
};

export const getBillStatusChoices = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `choices/bill_status`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    }
  );
  const response = await bxApiClient.get(url);

  const data = response.data?.result;
  return data.map((item: any) => ({
    value: item.key,
    label: item.value,
  }));
};
